import axios from 'axios'
import React, {
  createContext,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import { BrandConfig, EnvConfig } from 'src/configurations'
import { ExtendLocales } from 'src/enums'
import { ConfigResponse } from 'src/models/config.models'
import {
  ConfigState as AppConfigState,
  initialState as initalConfigState,
} from 'src/redux/reducers/data/config.reducer'
import { selectIsCspAuthenticatedFromState } from 'src/redux/selectors/auth.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'

type BaseRequestHeaders = {
  headers: {
    'X-Extend-Brand': string
    'X-Extend-App-ID': string
    'X-Extend-Platform': string
    'X-Extend-Platform-Version': string
    Accept: string
    'Content-Type': string
  }
  baseURL: string
}

type ConfigContextActions = {
  /** Refreshes application configuration */
  refreshConfigContext: () => Promise<void>
  setLocale: (locale: ExtendLocales) => void
  getBaseRequestHeaders: () => BaseRequestHeaders
}

type ConfigContextState = {
  environment: EnvConfig
  brand: BrandConfig
  locale: ExtendLocales
  appConfig: AppConfigState
}

export type ConfigContextValue = ConfigContextActions & ConfigContextState

const stubConfigContextActions: ConfigContextActions = {
  refreshConfigContext: () => {
    throw Error('ConfigContext Not Available')
  },
  setLocale: () => {
    throw Error('ConfigContext Not Available')
  },
  getBaseRequestHeaders: () => {
    throw Error('ConfigContext Not Available')
  },
}

const defaultConfigContextState: ConfigContextState = {
  environment:
    typeof document !== 'undefined'
      ? JSON.parse(document.getElementById('__APP_CONFIG__')?.innerHTML || '{}')
      : ({} as EnvConfig),
  brand:
    typeof document !== 'undefined'
      ? JSON.parse(
          document.getElementById('__BRAND_CONFIG__')?.innerHTML || '{}',
        )
      : ({} as BrandConfig),
  locale:
    typeof window !== 'undefined'
      ? (window.navigator.language as ExtendLocales)
      : ('en-US' as ExtendLocales),
  appConfig: initalConfigState,
}

export const ConfigContext = createContext<ConfigContextValue>({
  ...defaultConfigContextState,
  ...stubConfigContextActions,
})

type ConfigContextProviderProps = {
  initialState: ConfigContextState
  children: ReactNode
}

export default function ConfigContextProvider(
  props: ConfigContextProviderProps,
): ReactElement {
  const { children, initialState } = props
  const locationFromState = useSelector(selectLocationFromState)
  const cspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  const [configContextState, setConfigContextState] =
    useState<ConfigContextState>(initialState)
  const [didLoad, setDidLoad] = useState<boolean>(false)

  const getBaseRequestHeaders = (): BaseRequestHeaders => {
    return {
      headers: {
        'X-Extend-Brand': configContextState.brand.BRAND_ID,
        'X-Extend-App-ID': configContextState.brand.PRIMARY_DOMAIN,
        'X-Extend-Platform':
          cspAuthenticated || locationFromState.pathname.includes('/csp/')
            ? 'mira'
            : 'web',
        'X-Extend-Platform-Version':
          typeof navigator !== 'undefined' ? navigator?.userAgent || '' : '',
        Accept: 'application/vnd.paywithextend.v2021-03-12+json',
        'Content-Type': 'application/json',
      },
      baseURL:
        configContextState.environment.API_HOST ||
        'https://api-stage.paywithextend.com',
    }
  }

  const refreshConfigContext = async (): Promise<void> => {
    const baseHeaders = getBaseRequestHeaders()
    const requestConfig = {
      ...baseHeaders,
      headers: {
        ...baseHeaders.headers,
        'Content-Type': 'multipart/form-data',
      },
      baseURL: baseHeaders.baseURL,
    }
    const response = await axios.get<ConfigResponse>(
      `/config?${new URLSearchParams({ locale: configContextState.locale }).toString()}`,
      requestConfig,
    )

    const config: AppConfigState = {
      ...response.data,
    }
    setConfigContextState({
      ...configContextState,
      appConfig: config,
    })
  }

  useEffect(() => {
    if (didLoad === false) {
      refreshConfigContext()
      setDidLoad(true)
    }
  }, [didLoad])

  // NOTE:
  // I am unsure whether locale should be stored in
  // ConfigProvider or UserProvider
  // I don't know enough about the application to make
  // an informed decision right now, so I'm going to
  // keep this here for now and come to a decision later
  const setLocale = (locale: ExtendLocales) => {
    setConfigContextState({
      ...configContextState,
      locale,
    })
  }

  return (
    <ConfigContext.Provider
      value={{
        ...configContextState,
        refreshConfigContext,
        setLocale,
        getBaseRequestHeaders,
      }}
    >
      {children}
    </ConfigContext.Provider>
  )
}
